/**
 * Agape Energy Theme — product and cart behaviors
 */
(function () {
  'use strict';

  function getShopifyRoutes() {
    return (window.Shopify && window.Shopify.routes) || {
      cart_add_url: '/cart/add.js',
      cart_change_url: '/cart/change.js',
      cart_update_url: '/cart/update.js',
      cart_url: '/cart',
      checkout_url: '/checkout'
    };
  }

  function getDiscountHelpers() {
    return window.AgapeTheme || {};
  }

  function displayMoneyFromCents(cents) {
    const theme = getDiscountHelpers();
    const code = theme.getActiveDiscountCode ? theme.getActiveDiscountCode() : '';
    const percent = theme.getActiveDiscountPercent ? theme.getActiveDiscountPercent() : null;
    const format =
      theme.formatMoneyCents ||
      function (value) {
        return formatMoney(value);
      };

    if (code && percent && theme.applyDiscountDisplayCents) {
      return format(theme.applyDiscountDisplayCents(cents));
    }

    return formatMoney(cents);
  }

  function formatMoney(cents) {
    const dollars = cents / 100;
    if (Number.isInteger(dollars)) {
      return `$${dollars}`;
    }
    return `$${dollars.toFixed(2)}`;
  }

  function parseJsonAttribute(value, fallback) {
    if (!value) return fallback;

    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count <= 0;
    });
  }

  async function cartFetch(url, options) {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(options && options.headers ? options.headers : {})
      }
    });

    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(payload.description || payload.message || 'Could not update your cart.');
    }

    return payload;
  }

  async function getCart() {
    const routes = getShopifyRoutes();
    return cartFetch(`${routes.cart_url}.js`, { method: 'GET' });
  }

  async function addToCart(variantId, quantity, sellingPlanId) {
    const routes = getShopifyRoutes();
    const payload = { id: variantId, quantity };
    if (sellingPlanId) {
      payload.selling_plan = sellingPlanId;
    }

    return cartFetch(routes.cart_add_url, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async function changeCartLine(lineKey, quantity) {
    const routes = getShopifyRoutes();
    return cartFetch(routes.cart_change_url, {
      method: 'POST',
      body: JSON.stringify({ id: lineKey, quantity })
    });
  }

  function setBuyButtonsBusy(section, busy) {
    section.querySelectorAll('[data-desktop-submit], [data-sticky-submit]').forEach((button) => {
      if (busy) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        return;
      }

      const variantInput = section.querySelector('[data-variant-input]');
      const isAvailable = isVariantAvailable(variantInput);
      button.disabled = !isAvailable;
      button.setAttribute('aria-busy', 'false');
      button.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
    });
  }

  function isVariantAvailable(variantInput) {
    return variantInput && variantInput.getAttribute('data-variant-available') === 'true';
  }

  function updateBuyButtons(section, packOption) {
    const soldOutLabel = section.getAttribute('data-sold-out-label') || 'Sold Out';
    const variantInput = section.querySelector('[data-variant-input]');
    const isAvailable = packOption
      ? packOption.getAttribute('data-variant-available') === 'true'
      : isVariantAvailable(variantInput);
    const isSubscribe = packOption && packOption.getAttribute('data-is-subscribe') === 'true';

    section.querySelectorAll('[data-desktop-submit], [data-sticky-submit]').forEach((button) => {
      button.disabled = !isAvailable;
      button.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');

      if (!isAvailable) {
        button.textContent = soldOutLabel;
        return;
      }

      const defaultLabel = button.getAttribute('data-default-label');
      const subscribeLabel = button.getAttribute('data-subscribe-label') || 'Select Frequency →';
      button.textContent = isSubscribe ? subscribeLabel : defaultLabel;
    });
  }

  async function submitProductAddToCart(form) {
    const variantInput = form.querySelector('[data-variant-input]');
    const qtyInput = form.querySelector('[data-qty-input]');
    const sellingPlanInput = form.querySelector('[data-selling-plan-input]');
    const variantId = parseInt(variantInput ? variantInput.value : '', 10);
    const quantity = Math.max(1, parseInt(qtyInput ? qtyInput.value || '1' : '1', 10));
    const sellingPlanId = sellingPlanInput && !sellingPlanInput.disabled
      ? parseInt(sellingPlanInput.value || '', 10)
      : null;

    if (!variantId) {
      throw new Error('Please select a pack option.');
    }

    if (!isVariantAvailable(variantInput)) {
      throw new Error('This item is currently sold out.');
    }

    const section = form.closest('[data-agape-product]');
    const theme = getDiscountHelpers();

    if (theme.addToCartWithDrawer) {
      const packOption = section ? section.querySelector('[data-pack-option].is-selected') : null;
      const titleEl = section ? section.querySelector('.agape-product__title') : null;
      const productTitle = titleEl ? titleEl.textContent.trim() : '';
      const variantTitle = packOption ? packOption.getAttribute('data-variant-title') || '' : '';
      const unitPrice = packOption
        ? parseInt(packOption.getAttribute('data-variant-price') || '0', 10)
        : parseInt(variantInput.getAttribute('data-price') || '0', 10);
      const imageEl = section
        ? section.querySelector('[data-gallery-slide].is-active img, .product-gallery__image')
        : null;

      await theme.addToCartWithDrawer({
        variantId,
        quantity,
        sellingPlanId: sellingPlanId || undefined,
        productTitle,
        variantTitle,
        priceCents: unitPrice * quantity,
        imageSrc: imageEl ? imageEl.getAttribute('src') : '',
      });
      return;
    }

    const cart = await addToCart(variantId, quantity, sellingPlanId || undefined);
    updateCartCount(cart.item_count);

    const routes = getShopifyRoutes();
    window.location.href = routes.cart_url;
  }

  function bindCartQtyControls(root) {
    root.querySelectorAll('[data-cart-line]').forEach((line) => {
      const qtyControl = line.querySelector('[data-qty-control]');
      const qtyInput = line.querySelector('[data-qty-input]');
      const minus = qtyControl ? qtyControl.querySelector('[data-qty-minus]') : null;
      const plus = qtyControl ? qtyControl.querySelector('[data-qty-plus]') : null;
      const lineKey = line.getAttribute('data-line-key');

      if (!qtyInput || !lineKey) return;

      const applyQuantity = (nextQty) => {
        const quantity = Math.max(0, nextQty);
        if (quantity > 0) {
          qtyInput.value = String(quantity);
        }
        return changeCartLine(lineKey, quantity);
      };

      if (minus) {
        minus.addEventListener('click', () => {
          const current = parseInt(qtyInput.value || '1', 10);
          applyQuantity(current - 1).then(refreshCartSection).catch(showCartError);
        });
      }

      if (plus) {
        plus.addEventListener('click', () => {
          const current = parseInt(qtyInput.value || '1', 10);
          applyQuantity(current + 1).then(refreshCartSection).catch(showCartError);
        });
      }

      qtyInput.addEventListener('change', () => {
        const quantity = parseInt(qtyInput.value || '1', 10);
        applyQuantity(quantity).then(refreshCartSection).catch(showCartError);
      });
    });
  }

  function showCartError(error) {
    window.alert(error.message || 'Something went wrong. Please try again.');
  }

  function setCartBusy(busy) {
    document.querySelectorAll('[data-cart-checkout], [data-cart-remove], [data-qty-minus], [data-qty-plus], [data-qty-input]').forEach((el) => {
      el.disabled = busy;
      if (el.hasAttribute('data-cart-checkout')) {
        el.setAttribute('aria-busy', busy ? 'true' : 'false');
      }
    });
  }

  async function refreshCartSection() {
    const theme = getDiscountHelpers();
    if (theme.refreshCartSection) {
      return theme.refreshCartSection();
    }

    const section = document.querySelector('[data-agape-cart]');
    if (!section) return;

    const sectionId = section.getAttribute('data-section-id');
    if (!sectionId) {
      window.location.reload();
      return;
    }

    setCartBusy(true);

    try {
      const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
      if (!response.ok) {
        throw new Error('Could not refresh cart.');
      }

      const html = await response.text();
      const template = document.createElement('template');
      template.innerHTML = html.trim();
      const newSection = template.content.querySelector('[data-agape-cart]');

      if (!newSection) {
        throw new Error('Could not refresh cart.');
      }

      section.replaceWith(newSection);
      initCartPage();

      const cart = await getCart();
      updateCartCount(cart.item_count);

      const theme = getDiscountHelpers();
      if (theme.refreshDiscountPresentation) {
        theme.refreshDiscountPresentation(newSection);
      }
    } catch (error) {
      showCartError(error);
      window.location.reload();
    } finally {
      setCartBusy(false);
    }
  }

  function initCartPage() {
    const section = document.querySelector('.template-cart [data-agape-cart], .agape-cart-section [data-agape-cart]');
    if (!section || section.hasAttribute('data-cart-drawer-section')) return;

    const theme = getDiscountHelpers();
    if (theme.initCartInteractions) {
      theme.initCartInteractions(section);
    } else {
      bindCartQtyControls(section);
      section.querySelectorAll('[data-cart-remove]').forEach((button) => {
        button.addEventListener('click', () => {
          const lineKey = button.getAttribute('data-line-key');
          if (!lineKey) return;
          setCartBusy(true);
          changeCartLine(lineKey, 0)
            .then(refreshCartSection)
            .catch((error) => {
              setCartBusy(false);
              showCartError(error);
            });
        });
      });
    }

    if (theme.refreshDiscountPresentation) {
      theme.refreshDiscountPresentation(section);
    }
  }

  function ensureGallerySlide(slide) {
    if (!slide || slide.dataset.loaded === 'true') return;

    const src = slide.getAttribute('data-image-src');
    const srcset = slide.getAttribute('data-image-srcset');
    const alt = slide.getAttribute('data-image-alt') || '';
    if (!src) return;

    const img = document.createElement('img');
    img.className = 'product-gallery__image';
    img.alt = alt;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.src = src;
    if (srcset) img.srcset = srcset;
    img.sizes = slide.getAttribute('data-image-sizes') || '(min-width: 768px) 50vw, 100vw';

    slide.appendChild(img);
    slide.dataset.loaded = 'true';
  }

  function initProductGallery(gallery) {
    const slides = Array.from(gallery.querySelectorAll('[data-gallery-slide]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
    if (!slides.length) return;

    const prevBtn = gallery.querySelector('[data-gallery-prev]');
    const nextBtn = gallery.querySelector('[data-gallery-next]');
    const counter = gallery.querySelector('[data-gallery-count]');
    let current = 0;

    function showSlide(index) {
      const total = slides.length;
      const i = ((index % total) + total) % total;
      current = i;

      const slide = slides[i];
      if (slide) ensureGallerySlide(slide);

      slides.forEach((item, n) => {
        const isActive = n === i;
        item.hidden = !isActive;
        item.classList.toggle('is-active', isActive);
      });

      thumbs.forEach((thumb, n) => {
        thumb.classList.toggle('is-active', n === i);
      });

      if (counter) counter.textContent = (i + 1) + ' / ' + total;
    }

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = parseInt(thumb.getAttribute('data-index') || '0', 10);
        showSlide(index);
      });
    });

    if (prevBtn) prevBtn.addEventListener('click', () => showSlide(current - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => showSlide(current + 1));

    // swipe on touch
    const main = gallery.querySelector('.product-gallery__main');
    if (main && slides.length > 1) {
      let startX = null;
      let startY = null;
      main.addEventListener('touchstart', (event) => {
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      }, { passive: true });
      main.addEventListener('touchend', (event) => {
        if (startX === null) return;
        const dx = event.changedTouches[0].clientX - startX;
        const dy = event.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy)) {
          showSlide(current + (dx < 0 ? 1 : -1));
        }
        startX = null;
        startY = null;
      }, { passive: true });
    }

    showSlide(0);
  }

  let ingredientDetailsPromise;

  function getIngredientDetails(section) {
    if (!ingredientDetailsPromise) {
      const url = section ? section.getAttribute('data-ingredient-details-url') : null;
      if (!url) {
        ingredientDetailsPromise = Promise.resolve({});
      } else {
        ingredientDetailsPromise = fetch(url)
          .then((response) => (response.ok ? response.json() : {}))
          .catch(() => ({}));
      }
    }
    return ingredientDetailsPromise;
  }

  function renderIngredientDetail(text) {
    return text
      .split('\n\n')
      .map((paragraph) => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
      .join('');
  }

  function initIngredientAccordion() {
    document.querySelectorAll('[data-agape-product] [data-ingredient-toggle]').forEach((toggle) => {
      const item = toggle.closest('.ingredient-item');
      const detail = item ? item.querySelector('[data-ingredient-detail]') : null;
      const section = toggle.closest('[data-agape-product]');
      if (!detail || detail.dataset.bound === 'true') return;

      const setExpanded = async (expanded) => {
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        toggle.classList.toggle('is-expanded', expanded);
        detail.hidden = !expanded;

        if (!expanded || detail.dataset.loaded === 'true') return;

        const detailKey = detail.getAttribute('data-ingredient-key');
        if (!detailKey || !section) return;

        const details = await getIngredientDetails(section);
        const text = details[detailKey];
        if (!text) return;

        const body = detail.querySelector('[data-ingredient-detail-body]');
        if (body) {
          body.innerHTML = renderIngredientDetail(text);
          detail.dataset.loaded = 'true';
        }
      };

      toggle.addEventListener('click', () => {
        setExpanded(toggle.getAttribute('aria-expanded') !== 'true');
      });

      toggle.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setExpanded(toggle.getAttribute('aria-expanded') !== 'true');
        }
      });

      detail.dataset.bound = 'true';
    });
  }

  function initProductPage() {
    document.querySelectorAll('[data-agape-product]').forEach((section) => {
      const form = section.querySelector('[data-product-form]');
      const variantInput = section.querySelector('[data-variant-input]');
      const sellingPlanInput = section.querySelector('[data-selling-plan-input]');
      const qtyInput = section.querySelector('[data-qty-input]');
      const stickyTotal = section.querySelector('[data-sticky-total]');
      const stickyQty = section.querySelector('[data-sticky-qty]');
      const stickyPack = section.querySelector('[data-sticky-pack]');
      const packLabel = section.querySelector('[data-pack-label]');
      const packOptions = Array.from(section.querySelectorAll('[data-pack-option]'));
      const gallery = section.querySelector('[data-product-gallery]');
      const subscriptionModal = section.querySelector('[data-subscription-modal]');
      const subscriptionPlanList = section.querySelector('[data-subscription-plan-list]');
      const subscriptionPlanError = section.querySelector('[data-subscription-plan-error]');
      const subscriptionConfirm = section.querySelector('[data-subscription-confirm]');
      const subscriptionDismissButtons = Array.from(section.querySelectorAll('[data-subscription-dismiss]'));

      let selectedPrice = 0;
      let selectedTitle = '';
      let addToCartInFlight = false;
      let selectedPackOption = null;
      let activeSellingPlanOption = null;
      let modalResolve = null;
      let lastFocusedElement = null;

      function getSellingPlans(option) {
        const plans = parseJsonAttribute(option ? option.getAttribute('data-selling-plan-options') : '', []);
        return Array.isArray(plans) ? plans : [];
      }

      function getCheapestSellingPlanId(plans) {
        if (!plans.length) return null;

        let cheapestPlan = plans[0];
        let cheapestPrice = parseInt(cheapestPlan.price, 10) || 0;

        plans.slice(1).forEach((plan) => {
          const price = parseInt(plan.price, 10) || 0;
          if (price < cheapestPrice) {
            cheapestPlan = plan;
            cheapestPrice = price;
          }
        });

        return parseInt(cheapestPlan.id, 10) || null;
      }

      function getPreferredSellingPlanId(option, plans) {
        const selectedId = parseInt(
          option
            ? option.getAttribute('data-selected-selling-plan-id') || option.getAttribute('data-selling-plan-id') || ''
            : '',
          10
        );

        if (selectedId && plans.some((plan) => parseInt(plan.id, 10) === selectedId)) {
          return selectedId;
        }

        return getCheapestSellingPlanId(plans);
      }

      function syncSellingPlanInput(option) {
        if (!sellingPlanInput) return;

        const isSubscribe = option && option.getAttribute('data-is-subscribe') === 'true';
        const sellingPlans = getSellingPlans(option);
        const preferredSellingPlanId = getPreferredSellingPlanId(option, sellingPlans);

        if (isSubscribe && preferredSellingPlanId) {
          sellingPlanInput.value = String(preferredSellingPlanId);
          sellingPlanInput.disabled = false;
          option.setAttribute('data-selected-selling-plan-id', String(preferredSellingPlanId));
          return;
        }

        sellingPlanInput.value = '';
        sellingPlanInput.disabled = true;
      }

      function closeSubscriptionModal(selectedSellingPlanId) {
        if (!subscriptionModal || !modalResolve) return;

        subscriptionModal.hidden = true;
        section.classList.remove('is-subscription-modal-open');
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';

        const resolve = modalResolve;
        modalResolve = null;
        activeSellingPlanOption = null;

        if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
          lastFocusedElement.focus();
        }

        resolve(selectedSellingPlanId || null);
      }

      function renderSubscriptionPlans(option) {
        if (!subscriptionPlanList) return [];

        const sellingPlans = getSellingPlans(option);
        const preferredSellingPlanId = getPreferredSellingPlanId(option, sellingPlans);
        subscriptionPlanList.innerHTML = '';

        sellingPlans.forEach((plan, index) => {
          const optionId = `subscription-plan-${section.dataset.sectionId || 'section'}-${plan.id}-${index}`;
          const card = document.createElement('label');
          const input = document.createElement('input');
          const copy = document.createElement('span');
          const title = document.createElement('span');
          const detail = document.createElement('span');
          const price = document.createElement('span');
          const compare = document.createElement('span');
          const isChecked = parseInt(plan.id, 10) === preferredSellingPlanId;

          card.className = 'agape-product__subscription-option';
          card.setAttribute('for', optionId);

          input.type = 'radio';
          input.name = `subscription-plan-${section.dataset.sectionId || 'section'}`;
          input.id = optionId;
          input.value = String(plan.id);
          input.checked = isChecked;

          copy.className = 'agape-product__subscription-option-copy';
          title.className = 'agape-product__subscription-option-title';
          detail.className = 'agape-product__subscription-option-detail';
          price.className = 'agape-product__subscription-option-price';
          compare.className = 'agape-product__subscription-option-compare';

          const planPriceCents = parseInt(plan.price, 10) || 0;
          const compareAtPrice = parseInt(plan.compareAtPrice, 10) || 0;
          const theme = getDiscountHelpers();
          const discountPercent = theme.getActiveDiscountPercent ? theme.getActiveDiscountPercent() : null;
          const displayPlanCents =
            discountPercent && theme.applyDiscountDisplayCents
              ? theme.applyDiscountDisplayCents(planPriceCents)
              : planPriceCents;

          title.textContent = plan.name || 'Subscription';
          detail.textContent = 'Billed and shipped on this schedule.';
          price.textContent = formatMoney(displayPlanCents);

          if (compareAtPrice > planPriceCents) {
            compare.textContent = formatMoney(compareAtPrice);
          } else if (discountPercent && displayPlanCents < planPriceCents) {
            compare.textContent = formatMoney(planPriceCents);
          }

          copy.appendChild(title);
          copy.appendChild(detail);
          card.appendChild(input);
          card.appendChild(copy);

          const priceWrap = document.createElement('span');
          priceWrap.className = 'agape-product__subscription-option-pricing';
          if (compare.textContent) {
            priceWrap.appendChild(compare);
          }
          priceWrap.appendChild(price);
          card.appendChild(priceWrap);

          subscriptionPlanList.appendChild(card);
        });

        return sellingPlans;
      }

      function promptForSellingPlan(option) {
        if (!subscriptionModal || !subscriptionPlanList || !subscriptionConfirm) {
          return Promise.resolve(getPreferredSellingPlanId(option, getSellingPlans(option)));
        }

        const sellingPlans = renderSubscriptionPlans(option);
        if (!sellingPlans.length) {
          return Promise.resolve(null);
        }

        if (subscriptionPlanError) {
          subscriptionPlanError.hidden = true;
        }

        activeSellingPlanOption = option;
        lastFocusedElement = document.activeElement;
        subscriptionModal.hidden = false;
        section.classList.add('is-subscription-modal-open');
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';

        window.requestAnimationFrame(() => {
          const checkedInput = subscriptionPlanList.querySelector('input:checked');
          if (checkedInput) checkedInput.focus();
        });

        return new Promise((resolve) => {
          modalResolve = resolve;
        });
      }

      if (subscriptionConfirm) {
        subscriptionConfirm.addEventListener('click', () => {
          if (!subscriptionPlanList) {
            closeSubscriptionModal(null);
            return;
          }

          const selectedInput = subscriptionPlanList.querySelector('input:checked');
          if (!selectedInput) {
            if (subscriptionPlanError) {
              subscriptionPlanError.hidden = false;
            }
            return;
          }

          if (subscriptionPlanError) {
            subscriptionPlanError.hidden = true;
          }

          if (activeSellingPlanOption) {
            activeSellingPlanOption.setAttribute('data-selected-selling-plan-id', selectedInput.value);
          }

          closeSubscriptionModal(parseInt(selectedInput.value, 10));
        });
      }

      if (subscriptionPlanList) {
        subscriptionPlanList.addEventListener('change', () => {
          if (subscriptionPlanError) {
            subscriptionPlanError.hidden = true;
          }
        });
      }

      subscriptionDismissButtons.forEach((button) => {
        button.addEventListener('click', () => closeSubscriptionModal(null));
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && subscriptionModal && !subscriptionModal.hidden) {
          closeSubscriptionModal(null);
        }
      });

      function updateSticky() {
        if (!qtyInput || !stickyTotal) return;
        const qty = Math.max(1, parseInt(qtyInput.value || '1', 10));
        const totalCents = selectedPrice * qty;
        const theme = getDiscountHelpers();
        const priceEl = theme.getDiscountPriceElement
          ? theme.getDiscountPriceElement(stickyTotal)
          : stickyTotal.querySelector('[data-agape-price-cents]') || stickyTotal;

        priceEl.setAttribute('data-agape-price-cents', String(totalCents));
        priceEl.removeAttribute('data-agape-original-text');

        if (theme.refreshDiscountPresentation) {
          theme.refreshDiscountPresentation(section);
        } else {
          priceEl.textContent = formatMoney(totalCents);
        }

        if (stickyQty) stickyQty.textContent = String(qty);
        if (stickyPack) stickyPack.textContent = selectedTitle;
        if (packLabel) packLabel.textContent = selectedTitle;
      }

      function selectPack(option) {
        if (!option || !variantInput) return;

        selectedPackOption = option;
        packOptions.forEach((btn) => {
          const isSelected = btn === option;
          btn.classList.toggle('is-selected', isSelected);
          btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        variantInput.value = option.getAttribute('data-variant-id') || '';
        selectedPrice = parseInt(option.getAttribute('data-variant-price') || '0', 10);
        selectedTitle = option.getAttribute('data-variant-title') || '';
        const isAvailable = option.getAttribute('data-variant-available') === 'true';
        variantInput.setAttribute('data-variant-available', isAvailable ? 'true' : 'false');
        updateBuyButtons(section, option);

        syncSellingPlanInput(option);

        updateSticky();

        const theme = getDiscountHelpers();
        if (theme.refreshDiscountPresentation) {
          theme.refreshDiscountPresentation(section);
        }
      }

      if (packOptions.length) {
        const initial = packOptions.find((btn) => btn.classList.contains('is-selected')) || packOptions[0];
        selectPack(initial);

        packOptions.forEach((option) => {
          option.addEventListener('click', () => selectPack(option));
        });
      } else if (variantInput) {
        const firstVariantPrice = parseInt(variantInput.getAttribute('data-price') || '0', 10);
        selectedPrice = firstVariantPrice;
        selectedTitle = stickyPack ? stickyPack.textContent : '';
        updateSticky();
      }

      if (qtyInput) {
        const qtyControl = section.querySelector('[data-qty-control]');
        const minus = qtyControl ? qtyControl.querySelector('[data-qty-minus]') : null;
        const plus = qtyControl ? qtyControl.querySelector('[data-qty-plus]') : null;

        if (minus) {
          minus.addEventListener('click', () => {
            const next = Math.max(1, parseInt(qtyInput.value || '1', 10) - 1);
            qtyInput.value = String(next);
            updateSticky();
          });
        }

        if (plus) {
          plus.addEventListener('click', () => {
            const next = parseInt(qtyInput.value || '1', 10) + 1;
            qtyInput.value = String(next);
            updateSticky();
          });
        }

        qtyInput.addEventListener('change', updateSticky);
      }

      if (gallery) initProductGallery(gallery);

      if (form) {
        form.addEventListener('submit', async (event) => {
          event.preventDefault();

          if (addToCartInFlight) return;
          if (!isVariantAvailable(variantInput)) return;

          if (selectedPackOption && selectedPackOption.getAttribute('data-is-subscribe') === 'true') {
            if (!getSellingPlans(selectedPackOption).length) {
              window.alert('No subscription frequencies are currently available for this option.');
              return;
            }

            const selectedSellingPlanId = await promptForSellingPlan(selectedPackOption);
            if (!selectedSellingPlanId) {
              return;
            }

            if (sellingPlanInput) {
              sellingPlanInput.value = String(selectedSellingPlanId);
              sellingPlanInput.disabled = false;
            }
          } else if (sellingPlanInput) {
            sellingPlanInput.value = '';
            sellingPlanInput.disabled = true;
          }

          addToCartInFlight = true;
          setBuyButtonsBusy(section, true);

          submitProductAddToCart(form)
            .then(() => {
              addToCartInFlight = false;
              setBuyButtonsBusy(section, false);
            })
            .catch((error) => {
              addToCartInFlight = false;
              setBuyButtonsBusy(section, false);
              window.alert(error.message || 'Something went wrong. Please try again.');
            });
        });
      }

      updateSticky();

      const theme = getDiscountHelpers();
      if (theme.refreshDiscountPresentation) {
        theme.refreshDiscountPresentation(section);
      }
    });

    initIngredientAccordion();
  }


  function initVerseCarousel(root) {
    const track = root.querySelector('[data-verse-track]');
    const dotsWrap = root.querySelector('[data-verse-dots]');
    if (!track) return;

    const slides = Array.from(track.querySelectorAll('[data-verse-slide]'));
    if (!slides.length) return;

    const prevBtn = root.querySelector('[data-verse-prev]');
    const nextBtn = root.querySelector('[data-verse-next]');
    let current = 0;

    function go(index) {
      const total = slides.length;
      const i = ((index % total) + total) % total;
      current = i;
      track.style.transform = 'translateX(-' + i * 100 + '%)';

      const accent = getComputedStyle(slides[i]).getPropertyValue('--verse-accent').trim();
      if (dotsWrap) {
        dotsWrap.style.setProperty('--verse-dot', accent);
        Array.from(dotsWrap.children).forEach((dot, n) => {
          dot.classList.toggle('is-active', n === i);
        });
      }
      slides.forEach((slide, n) => slide.setAttribute('aria-hidden', n === i ? 'false' : 'true'));
    }

    if (dotsWrap) {
      slides.forEach((slide, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', 'Verse ' + (i + 1));
        dot.addEventListener('click', () => go(i));
        dotsWrap.appendChild(dot);
      });
    }

    if (prevBtn) prevBtn.addEventListener('click', () => go(current - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => go(current + 1));

    const viewport = root.querySelector('.agape-verse__viewport');
    if (viewport) {
      let startX = null;
      let startY = null;
      viewport.addEventListener('touchstart', (event) => {
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      }, { passive: true });
      viewport.addEventListener('touchend', (event) => {
        if (startX === null) return;
        const dx = event.changedTouches[0].clientX - startX;
        const dy = event.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy)) go(current + (dx < 0 ? 1 : -1));
        startX = null;
        startY = null;
      }, { passive: true });
    }

    // open on the verse matching the product you're looking at
    const handle = (root.getAttribute('data-current-flavor') || '').toLowerCase();
    let startIndex = 0;
    slides.forEach((slide, i) => {
      const key = slide.getAttribute('data-flavor');
      if (key && handle.indexOf(key) !== -1) startIndex = i;
    });
    go(startIndex);
  }

  function init() {
    initProductPage();
    document.querySelectorAll('[data-verse-carousel]').forEach(initVerseCarousel);
    initCartPage();

    const theme = getDiscountHelpers();
    if (theme.refreshDiscountPresentation) {
      theme.refreshDiscountPresentation(document);
    }

    document.addEventListener('agape:discount-code-active', () => {
      if (theme.refreshDiscountPresentation) {
        theme.refreshDiscountPresentation(document);
      }
    });

    if (!theme.refreshCartSection) {
      document.addEventListener('agape:cart-discount-sync-request', () => {
        refreshCartSection().catch(() => {});
      });
    }
  }

  window.AgapeProduct = {
    reinit: function () {
      initProductPage();
      initIngredientAccordion();
      document.querySelectorAll('[data-verse-carousel]').forEach(initVerseCarousel);
      var theme = getDiscountHelpers();
      if (theme.refreshDiscountPresentation) {
        theme.refreshDiscountPresentation(document);
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
